import { notify } from '@/components/core';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiFetch, interactiveFetch } from '../api/index.js';
import { StandardPageLayout, LoadingSpinner } from '../components/Common.jsx';
import {
  Box, Stack, TextField, InputAdornment,
  FormControl, Select, MenuItem,
  Tooltip, IconButton, Chip,
  Divider, Typography, Popover, Badge,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Radio, RadioGroup, FormControlLabel,
  Drawer
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import InsightSummary from '@/components/aiinsight/InsightSummary';

// ─── Action category → badge colour (Tailwind classes) ───────────────────────
const ACTION_STYLES = {
  create:  { bg: 'bg-emerald-50  dark:bg-emerald-950', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500' },
  update:  { bg: 'bg-blue-50     dark:bg-blue-950',    text: 'text-blue-700    dark:text-blue-300',    dot: 'bg-blue-500'    },
  delete:  { bg: 'bg-red-50      dark:bg-red-950',     text: 'text-red-700     dark:text-red-300',     dot: 'bg-red-500'     },
  execute: { bg: 'bg-amber-50    dark:bg-amber-950',   text: 'text-amber-700   dark:text-amber-300',   dot: 'bg-amber-500'   },
  login:   { bg: 'bg-violet-50   dark:bg-violet-950',  text: 'text-violet-700  dark:text-violet-300',  dot: 'bg-violet-500'  },
  logout:  { bg: 'bg-slate-100   dark:bg-slate-800',   text: 'text-slate-600   dark:text-slate-400',   dot: 'bg-slate-400'   },
  misc:    { bg: 'bg-gray-100    dark:bg-gray-800',    text: 'text-gray-600    dark:text-gray-400',    dot: 'bg-gray-400'    },
};

const categoryOf = (action = '') => {
  const a = action.toUpperCase();
  if (a.endsWith('_LOGIN'))                         return 'login';
  if (a.endsWith('_LOGOUT'))                        return 'logout';
  if (a.endsWith('_CREATED'))                       return 'create';
  if (a.endsWith('_DELETED') || a.endsWith('_REVOKED') || a.endsWith('_PURGED')) return 'delete';
  if (a.endsWith('_UPDATED') || a.endsWith('_CHANGED') || a.endsWith('_TOPUP') || a.endsWith('_APPROVED')) return 'update';
  if (a.endsWith('_EXECUTED') || a.endsWith('_TRIGGERED') || a.endsWith('_PUBLISHED') || a.endsWith('_SCHEDULED')) return 'execute';
  return 'misc';
};

const fmtAction = (action = '') =>
  action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

// ─── Action badge ─────────────────────────────────────────────────────────────
function ActionBadge({ action }) {
  const cat   = categoryOf(action);
  const style = ACTION_STYLES[cat] || ACTION_STYLES.misc;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${style.bg} ${style.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot}`} />
      {fmtAction(action)}
    </span>
  );
}

// ─── Entity type pill ─────────────────────────────────────────────────────────
function EntityPill({ type }) {
  return (
    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold tracking-widest uppercase bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
      {type}
    </span>
  );
}

// ─── Diff table for old / new values ─────────────────────────────────────────
function DiffTable({ old: oldVal, new: newVal }) {
  const empty = v => !v || (typeof v === 'object' && Object.keys(v).length === 0);
  if (empty(oldVal) && empty(newVal)) {
    return <p className="text-xs text-gray-400 dark:text-gray-500 italic">No change data captured.</p>;
  }

  const allKeys = Array.from(new Set([
    ...Object.keys(oldVal || {}),
    ...Object.keys(newVal || {}),
  ]));

  if (!allKeys.length) {
    return (
      <pre className="text-xs bg-gray-50 dark:bg-gray-800 rounded p-3 overflow-auto">
        {JSON.stringify(newVal || oldVal, null, 2)}
      </pre>
    );
  }

  return (
    <div className="overflow-auto rounded border border-gray-200 dark:border-gray-700">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-1/4">Field</th>
            <th className="px-3 py-2 text-left font-semibold text-red-500 uppercase tracking-wide w-[37.5%]">Before</th>
            <th className="px-3 py-2 text-left font-semibold text-emerald-500 uppercase tracking-wide w-[37.5%]">After</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {allKeys.map(k => {
            const before = oldVal?.[k];
            const after  = newVal?.[k];
            const changed = JSON.stringify(before) !== JSON.stringify(after);
            return (
              <tr key={k} className={changed ? 'bg-red-50/40 dark:bg-red-950/20' : ''}>
                <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">{k}</td>
                <td className="px-3 py-2 font-mono text-red-600 dark:text-red-400">
                  {before !== undefined ? JSON.stringify(before) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                </td>
                <td className="px-3 py-2 font-mono text-emerald-600 dark:text-emerald-400">
                  {after !== undefined ? JSON.stringify(after) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Expanded detail panel ────────────────────────────────────────────────────
function DetailPanel({ row }) {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const fetchSummary = async () => {
    setLoadingSummary(true);
    try {
      const data = await apiFetch(`/audit-logs/${row.id}/summary`);
      setSummary(data);
    } catch (err) {
      notify.error("Failed to generate AI summary.");
    } finally {
      setLoadingSummary(false);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900/60 p-5">
      <div className="flex flex-col gap-6">

        {/* Left — diff */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Change Diff
            </p>
            {row.changed_fields_count > 0 && (
              <span className="text-[10px] text-gray-500">{row.changed_fields_count} fields changed</span>
            )}
          </div>
          <DiffTable old={row.old_value} new={row.new_value} />
          
          {row.changes && Object.keys(row.changes).length > 0 && (
             <div className="mt-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">JSON Changes</p>
                <pre className="text-xs bg-gray-100 dark:bg-gray-800 rounded p-3 overflow-auto">
                  {JSON.stringify(row.changes, null, 2)}
                </pre>
             </div>
          )}

          {/* AI Summary Section */}
          <div className="mt-6 border border-indigo-100 dark:border-indigo-900/30 rounded-lg p-4 bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-indigo-700 dark:text-indigo-400 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                AI Audit Summary
              </p>
              {!summary && (
                <button 
                  onClick={fetchSummary}
                  disabled={loadingSummary}
                  className="text-[10px] px-2 py-1 rounded bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900 transition-colors disabled:opacity-50"
                >
                  {loadingSummary ? 'Generating...' : 'Generate Insight'}
                </button>
              )}
            </div>
            
            {summary && (
              <div className="space-y-3 mt-3">
                <div>
                  <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Summary</p>
                  <InsightSummary text={summary.summary} />
                </div>
                {summary.risk_analysis && (
                  <div>
                    <p className="text-[10px] font-bold text-gray-500 uppercase mt-2 mb-1">Risk Analysis</p>
                    <InsightSummary text={summary.risk_analysis} />
                  </div>
                )}
                {summary.recommended_actions && summary.recommended_actions.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-gray-500 uppercase mt-2 mb-1">Recommendations</p>
                    <InsightSummary text={summary.recommended_actions.map(act => `- ${act}`).join('\n')} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right — metadata */}
        <div className="space-y-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
              Event Metadata
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-gray-400 dark:text-gray-500 w-28 flex-shrink-0">Category</span>
                <span className="text-gray-700 dark:text-gray-300 font-medium">{row.category || '—'}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-gray-400 dark:text-gray-500 w-28 flex-shrink-0">Severity</span>
                <span className={`font-semibold ${row.severity === 'CRITICAL' ? 'text-red-600' : row.severity === 'HIGH' ? 'text-orange-500' : row.severity === 'MEDIUM' ? 'text-amber-500' : 'text-emerald-500'}`}>
                  {row.severity || 'INFO'}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-gray-400 dark:text-gray-500 w-28 flex-shrink-0">Risk Score</span>
                <span className="text-gray-700 dark:text-gray-300">{row.risk_score || 0}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-gray-400 dark:text-gray-500 w-28 flex-shrink-0">Source</span>
                <span className="text-gray-700 dark:text-gray-300">{row.source || 'SYSTEM'}</span>
              </div>
              {row.entity_id && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 w-28 flex-shrink-0">Entity ID</span>
                  <span className="font-mono text-gray-700 dark:text-gray-300 break-all">{row.entity_id}</span>
                </div>
              )}
              {row.correlation_id && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 w-28 flex-shrink-0">Correlation</span>
                  <span className="font-mono text-indigo-600 dark:text-indigo-400 break-all">{row.correlation_id}</span>
                </div>
              )}
              {row.actor_ip && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 w-28 flex-shrink-0">IP Address</span>
                  <span className="font-mono text-gray-700 dark:text-gray-300">{row.actor_ip}</span>
                </div>
              )}
              {row.reason && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 w-28 flex-shrink-0">Reason</span>
                  <span className="text-gray-700 dark:text-gray-300">{row.reason}</span>
                </div>
              )}
            </div>
          </div>
          
          {(row.session_context || row.api_context) && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
                Execution Context
              </p>
              <div className="space-y-2 text-sm bg-gray-100 dark:bg-gray-800 rounded p-3 overflow-auto font-mono text-xs text-gray-700 dark:text-gray-300">
                {row.session_context && <div><strong>Session:</strong> {JSON.stringify(row.session_context)}</div>}
                {row.api_context && <div><strong>API:</strong> {JSON.stringify(row.api_context)}</div>}
                {row.trace_context && <div><strong>Trace:</strong> {JSON.stringify(row.trace_context)}</div>}
              </div>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AuditLogsPage() {
  const { formatDate } = useTimezoneFormatter();

  const [rows,       setRows]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actions,    setActions]    = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);

  // Applied filter state
  const [actorSearch, setActorSearch] = useState('');
  const [action,      setAction]      = useState('');
  const [entityType,  setEntityType]  = useState('');
  const [fromDate,    setFromDate]    = useState('');
  const [toDate,      setToDate]      = useState('');
  const [page,        setPage]        = useState(0);
  const [pageSize,    setPageSize]    = useState(50);

  // Popover + pending filter state
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [pendingAction,     setPendingAction]     = useState('');
  const [pendingEntityType, setPendingEntityType] = useState('');
  const [pendingFromDate,   setPendingFromDate]   = useState('');
  const [pendingToDate,     setPendingToDate]     = useState('');

  const filterOpen = Boolean(filterAnchor);

  const debounceRef = useRef(null);

  // Export dialog state
  const [exportOpen, setExportOpen] = useState(false);
  const [exportType, setExportType] = useState('current');
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo,   setExportTo]   = useState('');
  const [exportLimit, setExportLimit] = useState(1000);

  // Count of active filters (excluding search — search stays inline)
  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (action)     c++;
    if (entityType) c++;
    if (fromDate)   c++;
    if (toDate)     c++;
    return c;
  }, [action, entityType, fromDate, toDate]);

  // Fetch filter options once
  useEffect(() => {
    apiFetch('/audit-logs/actions')
      .then(d => setActions(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch('/audit-logs/entity-types')
      .then(d => setEntityTypes(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (actorSearch) params.set('actor_id',    actorSearch);
    if (action)      params.set('action',      action);
    if (entityType)  params.set('entity_type', entityType);
    if (fromDate)    params.set('from_date',   new Date(fromDate).toISOString());
    if (toDate)      params.set('to_date',     new Date(toDate).toISOString());
    params.set('limit',  pageSize);
    params.set('offset', page * pageSize);

    interactiveFetch(`/audit-logs/?${params.toString()}`)
      .then(d => { setRows(d?.items || []); setTotal(d?.total || 0); setError(null); })
      .catch((err) => { setError(err.message || 'Failed to fetch data'); setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [actorSearch, action, entityType, fromDate, toDate, page, pageSize]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Debounce actor search
  const handleActorSearch = (val) => {
    setActorSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setPage(0), 500);
  };

  const executeExport = async () => {
    const params = new URLSearchParams();
    
    if (exportType === 'current') {
      if (actorSearch) params.set('actor_id',    actorSearch);
      if (action)      params.set('action',      action);
      if (entityType)  params.set('entity_type', entityType);
      if (fromDate)    params.set('from_date',   new Date(fromDate).toISOString());
      if (toDate)      params.set('to_date',     new Date(toDate).toISOString());
    } else if (exportType === 'date_range') {
      if (exportFrom) params.set('from_date', new Date(exportFrom).toISOString());
      if (exportTo)   params.set('to_date',   new Date(exportTo).toISOString());
    }
    params.set('limit', exportLimit);

    try {
      const data = await interactiveFetch(`/audit-logs/export?${params.toString()}`);
      const items = data.items || [];
      const jsonStr = JSON.stringify(items, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_export_${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setExportOpen(false);
    } catch (err) {
      console.error("Export failed", err);
      notify.error(`Export failed: ${err.message}`);
    }
  };

  // Filter popover handlers
  const handleOpenFilter = (e) => {
    setPendingAction(action);
    setPendingEntityType(entityType);
    setPendingFromDate(fromDate);
    setPendingToDate(toDate);
    setFilterAnchor(e.currentTarget);
  };

  const handleApplyFilters = () => {
    setAction(pendingAction);
    setEntityType(pendingEntityType);
    setFromDate(pendingFromDate);
    setToDate(pendingToDate);
    setPage(0);
    setFilterAnchor(null);
  };

  const handleClearFilters = () => {
    setAction('');
    setEntityType('');
    setFromDate('');
    setToDate('');
    setPendingAction('');
    setPendingEntityType('');
    setPendingFromDate('');
    setPendingToDate('');
    setPage(0);
    setFilterAnchor(null);
  };

  const totalPages = Math.ceil(total / pageSize);

  // ── Filter controls (injected into StandardPageLayout header) ──────────────
  const filterControls = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
      <TextField
        size="small"
        placeholder="Search actor email…"
        defaultValue={actorSearch}
        onChange={e => handleActorSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ width: 220, bgcolor: 'background.paper' }}
      />

      {/* ── Filter icon button with badge ── */}
      <Tooltip title="Filters">
        <IconButton
          onClick={handleOpenFilter}
          size="small"
          sx={{
            border: '1px solid',
            borderColor: activeFilterCount > 0 ? 'primary.main' : 'divider',
            borderRadius: '8px',
            bgcolor: activeFilterCount > 0 ? 'primary.50' : 'background.paper',
            px: 1.2, py: 0.8,
            '&:hover': { bgcolor: activeFilterCount > 0 ? 'primary.100' : 'action.hover' },
          }}
        >
          <Badge
            badgeContent={activeFilterCount}
            color="primary"
            sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
          >
            <TuneIcon sx={{ fontSize: 18 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* ── Clear filters text ── */}
      {activeFilterCount > 0 && (
        <button
          onClick={handleClearFilters}
          className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
        >
          <CloseIcon sx={{ fontSize: 14 }} />
          Clear Filters
        </button>
      )}

      <Tooltip title="Refresh Logs">
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshIcon sx={{ fontSize: 16 }} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </Tooltip>

      <Tooltip title="Export Options">
        <button
          onClick={() => setExportOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <FileDownloadIcon sx={{ fontSize: 16 }} />
          Export
        </button>
      </Tooltip>

      {/* ── Filter Popover ── */}
      <Popover
        open={filterOpen}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1, p: 2.5, borderRadius: '12px', minWidth: 340, maxWidth: 380,
              boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
            }
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            Filter Audit Logs
          </Typography>
          <IconButton size="small" onClick={() => setFilterAnchor(null)}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
              Action
            </Typography>
            <Select
              value={pendingAction}
              onChange={e => setPendingAction(e.target.value)}
              displayEmpty
              sx={{ borderRadius: '8px' }}
            >
              <MenuItem value="">All Actions</MenuItem>
              {actions.map(a => (
                <MenuItem key={a} value={a} sx={{ fontSize: '0.82rem' }}>{fmtAction(a)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
              Entity Type
            </Typography>
            <Select
              value={pendingEntityType}
              onChange={e => setPendingEntityType(e.target.value)}
              displayEmpty
              sx={{ borderRadius: '8px' }}
            >
              <MenuItem value="">All Types</MenuItem>
              {entityTypes.map(t => (
                <MenuItem key={t} value={t} sx={{ fontSize: '0.82rem' }}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
              Date Range
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="datetime-local"
                label="From"
                value={pendingFromDate}
                onChange={e => setPendingFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
              <TextField
                size="small"
                type="datetime-local"
                label="To"
                value={pendingToDate}
                onChange={e => setPendingToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <button
            onClick={handleClearFilters}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={handleApplyFilters}
            className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors shadow-sm"
          >
            Apply Filters
          </button>
        </Stack>
      </Popover>
    </Stack>
  );

  return (
    <StandardPageLayout
      title={
        <div className="flex items-center gap-3">
          Audit Logs
          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold tracking-wide bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300">
            {total.toLocaleString()}
          </span>
          {loading && (
            <div className="ml-1 opacity-70">
              <LoadingSpinner size="sm" />
            </div>
          )}
        </div>
      }
      description="Immutable record of all data mutations across the platform."
      filterControls={filterControls}
      scrollable={false}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col flex-1 min-h-0 h-full">
        {/* Table */}
        <div className="overflow-auto flex-1 relative">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Severity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Entity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Name / ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Actor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={7} className="py-20 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Failed to load audit logs</p>
                      <p className="text-xs text-red-500 dark:text-red-400 max-w-md">{error}</p>
                      <button onClick={fetchLogs} className="mt-2 text-xs font-medium px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        Try Again
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-20 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">No audit entries found.</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">Try adjusting your filters.</p>
                    </div>
                  </td>
                </tr>
              )}
              {rows.map(row => (
                <React.Fragment key={row.id}>
                  <tr
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer ${selectedRow?.id === row.id ? 'bg-indigo-50/30 dark:bg-indigo-950/20' : ''}`}
                    onClick={() => setSelectedRow(row)}
                  >
                    {/* Category */}
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">{row.category || '—'}</span>
                    </td>

                    {/* Severity */}
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider border ${
                        row.severity === 'CRITICAL' ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800' :
                        row.severity === 'HIGH' ? 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800' :
                        row.severity === 'MEDIUM' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800' :
                        row.severity === 'LOW' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800' :
                        'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
                      }`}>
                        {row.severity || 'INFO'}
                      </span>
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3">
                      <ActionBadge action={row.action} />
                    </td>

                    {/* Entity type */}
                    <td className="px-4 py-3">
                      <EntityPill type={row.entity_type} />
                    </td>

                    {/* Name / ID */}
                    <td className="px-4 py-3">
                      <Tooltip title={row.entity_name ? `ID: ${row.entity_id}` : ''} placement="top">
                        <span className="text-sm text-gray-700 dark:text-gray-300 max-w-[220px] truncate block font-medium">
                          {row.entity_name || row.entity_id || '—'}
                        </span>
                      </Tooltip>
                    </td>

                    {/* Actor */}
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
                        {row.actor_id || '—'}
                      </span>
                    </td>

                    {/* Time */}
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate ? formatDate(row.created_at) : new Date(row.created_at).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="shrink-0 flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 dark:text-gray-400">Rows per page:</span>
            <FormControl size="small">
              <Select
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
                sx={{ fontSize: '0.78rem', '& .MuiSelect-select': { py: '2px', px: '8px' } }}
              >
                {[25, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
          </div>

          <span className="text-xs text-gray-500 dark:text-gray-400">
            {total === 0 ? '0–0 of 0' : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)} of ${total.toLocaleString()}`}
          </span>

          <div className="flex items-center gap-1">
            <button
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
              className="p-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
              className="p-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Drawer for Details */}
      <Drawer
        anchor="right"
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 500, md: 650 }, bgcolor: 'background.default' }
        }}
      >
        {selectedRow && (
          <div className="flex flex-col h-full bg-white dark:bg-gray-900">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <Typography variant="subtitle1" fontWeight={700}>Audit Log Detail</Typography>
              <IconButton size="small" onClick={() => setSelectedRow(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            <div className="flex-1 overflow-y-auto">
               <DetailPanel row={selectedRow} />
            </div>
          </div>
        )}
      </Drawer>

      {/* Export Dialog */}
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Export Audit Logs</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ py: 1 }}>
            <FormControl>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Export Scope</Typography>
              <RadioGroup value={exportType} onChange={e => setExportType(e.target.value)}>
                <FormControlLabel value="current" control={<Radio size="small" />} label={<Typography variant="body2">Current Filtered View</Typography>} />
                <FormControlLabel value="date_range" control={<Radio size="small" />} label={<Typography variant="body2">Custom Date Range</Typography>} />
              </RadioGroup>
            </FormControl>

            {exportType === 'date_range' && (
              <Stack direction="row" spacing={2}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={exportFrom}
                  onChange={e => setExportFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={exportTo}
                  onChange={e => setExportTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )}

            <FormControl size="small" fullWidth>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Maximum Records</Typography>
              <Select value={exportLimit} onChange={e => setExportLimit(Number(e.target.value))}>
                <MenuItem value={100}>100 records</MenuItem>
                <MenuItem value={1000}>1,000 records</MenuItem>
                <MenuItem value={5000}>5,000 records</MenuItem>
                <MenuItem value={10000}>10,000 records</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExportOpen(false)} color="inherit" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={executeExport} variant="contained" color="primary" sx={{ textTransform: 'none', boxShadow: 'none' }}>Export Data</Button>
        </DialogActions>
      </Dialog>
    </StandardPageLayout>
  );
}